import { styled } from "styled-components";

import { useTranslate } from "common/utilities/useTranslate";
import { interpolate } from "domains/language/interpolate";
import { COPYRIGHT_DEFAULT_TEXT } from "domains/topicDetail/components/TMCopyrightText";
import { getTopicCurator } from "domains/topicDetail/utils";
import type { Topic } from "entities/topics/types";

const StyledText = styled.p`
  font-size: 0.9rem;
  line-height: 19px;
  color: ${({ theme }) => theme.colors.fillPrimary};
  margin: 0 0 5px 0;
`;

type KeyIssueCopyrightTextProps = { keyIssue: Topic; topic: Topic };

/**
 * Variations of copyright text:
 *  - Key issue belongs to the map (i.e. same co_curator).
 * "This issue is defined and incorporated into this transformation map by ${co_curator}."
 *
 *  - Key issue is borrowed from a different map (i.e. different co_curator).
 * "This issue is defined by ${co_curator of key issue's parent_topic} and incorporated into
 * this transformation map by ${topic's co_curator}."
 *
 *  - Key issue is custom-created by user on a Custom map
 * "This issue is defined by ${topic's creator} and does not necessarily reflect the view of
 * the World Economic Forum."
 *
 * Glossary:
 *  - co_curator - The person or organisation that helped curate the map. For Forum maps,
 * this comes from Salesforce (or defaults to "World Economic Forum" if not present). In the case
 * of this component, for Custom maps we have just used the map's creator (topic.provider?.name).
 *
 *  - "Borrowed" key issues - Key issue's have a single parent_topic to denote which map
 * they originally were created for. In the case of "borrowed" key issues, the key issue has
 * been re-used for a different map (which could also be a Custom map) than its original parent_topic.
 */
export const KeyIssueCopyrightText = ({ keyIssue, topic }: KeyIssueCopyrightTextProps) => {
  const translate = useTranslate();

  const keyIssueCurator = keyIssue.parent_topic?.co_curator ?? "World Economic Forum";
  const topicCurator = getTopicCurator(topic);

  const termName = getTermName(!!keyIssue.is_custom_dimension, topicCurator, keyIssueCurator);
  const text = interpolate(translate(termName), {
    curator: () => keyIssueCurator,
    creator: () => topicCurator,
  });

  return (
    <>
      <StyledText data-testid="key-issue-copyright-text">{text}</StyledText>
      {!keyIssue.is_custom_dimension && <StyledText>{COPYRIGHT_DEFAULT_TEXT}</StyledText>}
    </>
  );
};

const getTermName = (isCustomKeyIssue: boolean, topicCurator: string, keyIssueCurator: string) => {
  if (isCustomKeyIssue) return "app.transformation_map.copyright.key_issue.custom";
  if (keyIssueCurator === topicCurator) return "app.transformation_map.copyright.key_issue.curator_only";
  return "app.transformation_map.copyright.key_issue.curator_and_creator";
};
